import React, { useEffect } from 'react';
import { JBAlert, JBButton, JBTypography } from '@jbhi-fi/lanyard-ui';
import { useTracking, trackingContext } from '@jbhi-fi/components/src/Tracking';
import { buildSupportRequestUrl } from '../../../../../../utils/zendesk/buildSupportRequestUrl/buildSupportRequestUrl';
import { SupportRequestPrefillData } from '../../../../../../utils/zendesk/buildSupportRequestUrl/types';
import { styles } from './SupportRequestAlert.css';

export type SupportErrorAlertProps = {
  type?: 'warning' | 'error';
  heading: string;
  content: string;
  inlineSupportLink?: boolean;
  supportRequestLabel: string;
  supportRequestId: string;
  supportRequestPrefilledEnquiryType?: string;
  supportRequestPrefilledSubject?: string;
  supportRequestPrefilledAccountEnquiryType?: string;
};
/*
  An error / warning alert signifying that something went wrong, giving the customer an option of submitting a support request.
  supportRequestHref / label define the button the user can click to open the support article / ticket page in a new tab.
 */
export const SupportRequestAlert = ({
  type = 'error',
  heading,
  content,
  inlineSupportLink = false,
  supportRequestLabel,
  supportRequestId,
  supportRequestPrefilledEnquiryType,
  supportRequestPrefilledSubject,
  supportRequestPrefilledAccountEnquiryType,
}: SupportErrorAlertProps): JSX.Element => {
  const { tracker } = useTracking({
    context: trackingContext.account,
    category: 'my account',
    group: heading,
  });

  useEffect(() => {
    tracker.trackView();
    tracker.trackError({ errorMsg: content });
  }, []);

  const getSupportRequestPrefillData = (): SupportRequestPrefillData => {
    return {
      email: window.Shopify?.Customer?.email ?? '',
      firstName: window.Shopify?.Customer?.firstName ?? '',
      lastName: window.Shopify?.Customer?.lastName ?? '',
      subject: supportRequestPrefilledSubject ?? '',
      enquiryType: supportRequestPrefilledEnquiryType ?? '',
      accountEnquiryType: supportRequestPrefilledAccountEnquiryType ?? '',
    };
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    tracker.trackClick({ label: supportRequestLabel });
    window.open(
      buildSupportRequestUrl({ formId: supportRequestId, prefillData: getSupportRequestPrefillData() }),
      '__blank'
    );
  };

  return (
    <JBAlert type={type} heading={heading} showIcon closable classes={styles.container}>
      <JBTypography type="p" margin="0 0 1em 0">
        {content}
        {inlineSupportLink ? <>&nbsp;</> : ''}
        <JBButton type="link" classes={styles.supportButton({ inlineSupportLink })} onClick={handleClick}>
          <JBTypography type="p" margin="0 0 .15em 0">
            {supportRequestLabel}.
          </JBTypography>
        </JBButton>
      </JBTypography>
    </JBAlert>
  );
};
